import { runtimeBus } from '../../app/runtimeBus';
import { getCosmetic, RARITIES, RARITY_COLORS } from '../../game/content/cosmetics';
import type { DrawReceipt } from '../../game/storage/progressStore';
import { escapeHtml, itemPreview } from './cosmeticMarkup';

export class GachaPresentationController {
  private finish: (() => void) | null = null;
  private frame = 0;
  private readonly visibility = (): void => { if (document.hidden) this.skip(); };
  skip(): void { this.finish?.(); }
  async play(host: HTMLElement, receipt: DrawReceipt): Promise<void> {
    this.skip();
    const items = receipt.pulls.map(pull => getCosmetic(pull.itemId)).filter(item => item !== undefined);
    const hero = items.reduce((best, item) => RARITIES.indexOf(item.rarity) > RARITIES.indexOf(best.rarity) ? item : best, items[0]!);
    if (!hero) return;
    const rank = RARITIES.indexOf(hero.rarity);
    const ten = receipt.count === 10;
    const heroIndex = items.indexOf(hero);
    const revealOrder = items.filter((_, index) => index !== heroIndex);
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = reduced ? 250 : (ten ? 7000 : 4000) + Math.max(0, rank - 3) * 1000;
    host.innerHTML = `<div class="observation-stage" data-rarity="${hero.rarity}" data-phase="gather" style="--rarity-color:${RARITY_COLORS[hero.rarity]}">
      <canvas class="observation-particles" aria-hidden="true"></canvas>
      <div class="observation-orbit observation-orbit--one"></div><div class="observation-orbit observation-orbit--two"></div><div class="observation-orbit observation-orbit--three"></div>
      <div class="observation-focus"><div class="observation-aura"></div><img class="observation-crystal" src="/assets/gacha/fx/observation-stone.webp" alt=""/>
      <div class="observation-hero">${itemPreview(hero)}<span class="observation-rarity">${hero.rarity}</span><h3>${escapeHtml(hero.name)}</h3></div></div>
      <p class="observation-caption" role="status">観測石に、意志が集まる。</p>
      ${ten ? `<div class="observation-shards">${revealOrder.map((item, i) => `<span style="--shard-color:${RARITY_COLORS[item.rarity]};--shard-index:${i}"><img src="${item.thumbnail}" alt=""/><b>${item.rarity}</b></span>`).join('')}<span class="observation-shard--sealed" style="--shard-color:${RARITY_COLORS[hero.rarity]};--shard-index:9"><b>◆</b></span></div>` : ''}
      <button type="button" class="economy-button observation-skip" data-action="skip">スキップ <span aria-hidden="true">≫</span></button>
    </div>`;
    const stage = host.querySelector<HTMLElement>('.observation-stage')!;
    host.querySelector<HTMLButtonElement>('[data-action="skip"]')!.focus({ preventScroll: true });
    const canvas = host.querySelector<HTMLCanvasElement>('canvas')!;
    const ctx = canvas.getContext('2d');
    const bounds = stage.getBoundingClientRect();
    const width = Math.max(1, bounds.width), height = Math.max(1, bounds.height);
    const dpr = Math.min(2, devicePixelRatio);
    canvas.width = width * dpr; canvas.height = height * dpr;
    ctx?.scale(dpr, dpr);
    const count = width < 600 ? 60 : 160;
    const particles = Array.from({ length: count }, (_, i) => ({ angle: i * 2.39996, radius: 0.25 + (i * 37 % 100) / 100 * .55, speed: .4 + (i % 9) / 10, size: 1 + i % 3 }));
    const caption = host.querySelector<HTMLElement>('.observation-caption')!;
    // Original recording's strongest 20ms window begins at 2.02s.
    const openingAt = 2020;
    const revealAt = ten ? 5160 : openingAt;
    if (!reduced) runtimeBus.gachaAudio?.play();
    document.addEventListener('visibilitychange', this.visibility);
    return new Promise(resolve => {
      let ended = false;
      this.finish = () => {
        if (ended) return;
        ended = true; cancelAnimationFrame(this.frame);
        document.removeEventListener('visibilitychange', this.visibility);
        runtimeBus.gachaAudio?.stop();
        canvas.remove(); this.finish = null; resolve();
      };
      const start = performance.now();
      let phase = '';
      let heroAudioStarted = false;
      const render = (now: number) => {
        if (ended) return;
        const elapsed = now - start;
        if (ten && !reduced && !heroAudioStarted && elapsed >= revealAt - openingAt) {
          heroAudioStarted = true;
          runtimeBus.gachaAudio?.play();
        }
        const nextPhase = elapsed < 1150 ? 'gather' : elapsed < 1720 ? 'rise' : elapsed < openingAt ? 'hold' : elapsed < revealAt ? 'shards' : 'reveal';
        if (phase !== nextPhase) {
          phase = nextPhase; stage.dataset.phase = phase;
          caption.textContent = phase === 'reveal' ? '新たな盤面を、観測しました。' : phase === 'shards' ? '十の可能性が、ひらく。' : phase === 'hold' ? 'その向こうに、まだ見ぬ世界。' : '観測石に、意志が集まる。';
        }
        if (ctx && !reduced) {
          ctx.clearRect(0, 0, width, height);
          const t = elapsed / 1000;
          const opening = elapsed >= revealAt;
          const spread = opening ? 1 + Math.min(2, (elapsed - revealAt) / 800) : Math.max(.12, 1 - elapsed / 3800);
          ctx.fillStyle = RARITY_COLORS[hero.rarity];
          ctx.globalCompositeOperation = 'lighter';
          for (const p of particles) {
            const angle = p.angle + t * p.speed * (opening ? .12 : .32);
            const radius = p.radius * Math.min(width, height) * .7 * spread;
            const x = width / 2 + Math.cos(angle) * radius;
            const y = height * .43 + Math.sin(angle) * radius * .7;
            ctx.globalAlpha = Math.max(0, Math.min(.8, opening ? 1 - (elapsed - revealAt) / 1800 : .3 + Math.sin(t * 2 + p.angle) * .2));
            ctx.fillRect(x, y, p.size, p.size);
          }
          ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
        }
        if (elapsed >= duration) { this.finish?.(); return; }
        this.frame = requestAnimationFrame(render);
      };
      this.frame = requestAnimationFrame(render);
    });
  }
}
